import { ChangeDetectorRef, Component, Input } from '@angular/core';
import { LearningResourceViewModel } from '@app/inputs/models/learning-resource.view-model';
import { OrgEndorsedService } from '@app/orgs/services/org-endorsed.service';
import { PathwayAddContentService } from '@app/pathways/services/pathway-add-content.service';
import { SearchService } from '@app/search/services/search.service';
import { AuthService } from '@app/shared/services/auth.service';
import { LDFlagsService } from '@app/shared/services/ld-flags.service';
import { TranslateService } from '@ngx-translate/core';
import { OrgManagedContentService } from '../org-managed-notification/org-managed-content.service';
import { SearchFiltersStateService } from '../search-filters/search-filters-state.service';
import { SearchFilterId } from '../search-filters/search-filters.component';
import { AddBySearchBaseComponent } from './add-by-search-base.component';

@Component({
  selector: 'dgx-add-by-search',
  templateUrl: './add-by-search-base.component.html',
})
export class AddBySearchComponent extends AddBySearchBaseComponent {
  public static readonly modalName = 'search';
  public static readonly debounceDuration = 300;
  public static readonly typeFacetId = 'Type';
  public static readonly internalFacetId = 'Internal';
  public static readonly searchParams = {
    queryOffset: 0,
    itemsPerPage: 40,
  };

  @Input() public resourceType: 'Pathway' | 'Target';

  public i18n = this.translateService.instant([
    'Pathways_HoldForLater',
    'TargetResourcesForm_Selected',
    'Pathways_SearchCatalog',
  ]);
  public searchCatalogText = this.i18n.Pathways_SearchCatalog;
  public selectedText = this.i18n.TargetResourcesForm_Selected;
  public holdForLaterText = this.i18n.Pathways_HoldForLater;

  constructor(
    // Parent services for component being extended
    authService: AuthService,
    cdr: ChangeDetectorRef,
    ldFlagsService: LDFlagsService,
    orgEndorsedService: OrgEndorsedService,
    searchService: SearchService,
    orgManagedService: OrgManagedContentService,
    // Local-only services
    private pathwayAddContentService: PathwayAddContentService,
    private searchFiltersStateService: SearchFiltersStateService,
    private translateService: TranslateService
  ) {
    super(
      authService,
      cdr,
      ldFlagsService,
      orgEndorsedService,
      searchService,
      orgManagedService
    );
  }

  /**
   * Local implementation of updateButtonText.
   *
   * @param savedItemList
   */
  public updateButtonText(savedItemList: LearningResourceViewModel[]) {
    this.addItemsButtonText =
      this.pathwayAddContentService.updateButtonText(savedItemList);
  }

  /**
   * Local implementation of search.
   *
   * @param searchTerm
   * @returns
   */
  protected search(searchTerm: string) {
    this.searchTerm = searchTerm;
    this.isSearchOpen = true;
    this.isSearching = true;
    const selectedFacets = this.searchService.filtersToFacets(
      this.selectedFilters
    );
    const isRecent = false;
    const isPopular = false;
    const query = this.searchService.search(
      this.searchTerm,
      selectedFacets,
      AddBySearchComponent.searchParams.itemsPerPage,
      AddBySearchComponent.searchParams.queryOffset,
      this.authService.authUser.defaultOrgId,
      isRecent,
      isPopular,
      this.searchFiltersStateService.ignorePreferredLanguage,
      this.inputsOnly,
      this.savedItems.map((item) => {
        return {
          resourceId: item.resourceId,
          resourceType: item.resourceType,
        };
      })
    );

    query.subscribe({
      next: (data) => {
        // Always display all `Type` subitems, otherwise bookmarked searches
        // and page reloads won't display any subitems that aren't pre-selected
        const configs = this.searchService.patchFacetConfig(
          SearchFilterId.Type,
          data.facets,
          { showEmptyOptions: true }
        );
        // bind results
        this.searchResults = data.results as LearningResourceViewModel[];
        this.searchFilters = this.searchService
          .facetsToFilters(data.facets, configs)
          .map((filter) => {
            // When adding content to a Pathway through the Add to content Modal we shouldn’t
            // be able to Add Pathways and Plans within the Search type filter
            if (
              filter.model.id === SearchFilterId.Type &&
              this.resourceType === 'Pathway'
            ) {
              return {
                ...filter,
                subitems: filter.subitems.filter(
                  (i) => i.title !== 'Pathway' && i.title !== 'Target'
                ),
              };
            }
            return filter;
          });
        this.isSearching = false;
      },
      error: () => {
        this.isSearching = false;
      },
      complete: () => {
        this.cdr.markForCheck();
      },
    });
    return query;
  }

  /**
   * Local implementation of resetFilters.
   */
  protected resetFilters() {
    this.invalidateFilterCache = true;
    this.searchFiltersStateService.setIgnorePreferredLanguage(false);
    this.selectedFilters = [];
  }
}
